<template>
  <button type="button" class="btn btn-primary" @click="showModal()"><font-awesome-icon icon="file-download" /></button>

  <!-- Generate Dokument Modal -->
  <div
    class="modal fade"
    :id="'downloadVertragDocumentModal-' + dokument.id"
    tabindex="-1"
    aria-labelledby="downloadVertragDocumentModal"
    aria-hidden="true"
    ref="downloadVertragDocumentModalRef"
  >
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header bg-primary py-2">
          <strong class="modal-titel my-auto">Druckvorlage auswählen</strong>

          <button type="button" class="btn-close btn-sm text-white" @click="closeModal()"></button>
        </div>

        <div class="modal-body modal-body-min-height" id="downloadVertragDocumentModalBody">
          <LoadingSpinner v-if="loading" />

          <div v-else>
            <div v-if="errorMsg != ''" class="alert alert-danger mb-3">
              Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.
              {{ errorMsg }}
            </div>

            <div class="row mb-3">
              <div class="col-4 offset-4">
                <div class="input-group input-group-sm my-auto">
                  <input v-model.trim="searchString" type="search" class="form-control" id="searchInput" placeholder="Suche..." />
                  <span class="input-group-text">
                    <font-awesome-icon icon="magnifying-glass" class="p-1" />
                  </span>
                </div>
              </div>
            </div>

            <div v-if="sortedDokumente.length == 0" class="alert alert-info mb-3">Es wurden keine Word-Vorlagen gefunden.</div>

            <div v-for="(value, key) in groupedItems" :key="key" class="mb-4">
              <h4>{{ key }}</h4>

              <div class="row mx-0 mb-1">
                <div class="col-3 fw-bold px-2 my-auto">Titel</div>
                <div class="col-5 fw-bold px-2 my-auto">Gruppe</div>
                <div class="col-4 fw-bold px-2 my-auto">Datei</div>
              </div>

              <div v-for="dokument in value" :key="dokument.id">
                <div class="row bg-white shadow-sm mx-1 mb-1 py-2 select-word-vorlage" @click="generateDocument(dokument.id)">
                  <div class="col-3 my-auto">{{ dokument.titel }}</div>
                  <div class="col-5 my-auto">
                    <span v-for="gruppe in getGruppen(dokument)" :key="gruppe" class="badge text-bg-primary me-1 mb-1">{{ gruppe }}</span>
                  </div>
                  <div class="col-4 my-auto">{{ dokument.fileName }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  //#region Imports
  import { PropType, computed, ref } from 'vue';
  import { Modal } from 'bootstrap';
  import { useStore } from '@/store';

  import LoadingSpinner from '../LoadingSpinner.vue';

  import { ActionTypes as VertragActionTypes } from '@/store/modules/Vertraege/actions';

  import { GenerateWordDocumentRequest, VertragDokument, Vertragspartei } from '@/models/VertragModels';
  import { Druckvorlage, GroupedDruckvorlagen } from '@/models/DruckvorlageModels';
  import { Vertragspartner } from '@/models/VertragspartnerModels';
  //#endregion

  const props = defineProps({
    dokument: {
      type: Object as PropType<VertragDokument>,
      required: true,
    },
    titel: {
      type: String as PropType<string>,
      required: true,
    },
    qrCodeData: {
      type: String as PropType<string>,
      required: true,
    },
    vertragspartner2Name: {
      type: String as PropType<string>,
      required: true,
    },
    vertragspartei: {
      type: Object as PropType<Vertragspartei | null>,
      required: true,
    },
  });

  const store = useStore();

  const loading = computed(() => store.getters.status.createVertragDokumentLoading);
  const errorMsg = computed(() => store.getters.status.vertragErrorMsg);

  const vertragspartner = computed(() => {
    if (props.vertragspartei == undefined) {
      return null;
    }

    const p = props.vertragspartei;

    const partner = store.getters.vertragspartner.filter(
      (vp: Vertragspartner) =>
        vp.unternehmen.trim().toLowerCase() == p.unternehmen.trim().toLowerCase() &&
        vp.unternehmenzusatz.trim().toLowerCase() == p.unternehmenzusatz.trim().toLowerCase() &&
        vp.name.trim().toLowerCase() == p.name.trim().toLowerCase() &&
        vp.vorname.trim().toLowerCase() == p.vorname.trim().toLowerCase()
    );

    return partner.length > 0 ? partner[0] : null;
  });

  function getGruppen(druckvorlage: Druckvorlage): string[] {
    const uniqueGruppen = new Array<string>();
    if (druckvorlage.kategorie == undefined || druckvorlage.kategorie.trim() == '') {
      return uniqueGruppen;
    }

    const gruppen = druckvorlage.kategorie.split(';');

    gruppen.forEach((gruppe) => {
      const trimmedGruppe = gruppe.trim();
      if (trimmedGruppe !== '' && !uniqueGruppen.includes(trimmedGruppe)) {
        uniqueGruppen.push(trimmedGruppe);
      }
    });

    return uniqueGruppen.sort();
  }

  const searchString = ref('');

  const sortByValue = ref('titelasc');
  const druckvorlagen = computed(() => {
    if (vertragspartner.value == undefined || vertragspartner.value.druckvorlagen == undefined || vertragspartner.value.druckvorlagen.length == 0) {
      return store.getters.druckvorlagen;
    } else {
      const allowedDruckvorlagenIds = vertragspartner.value.druckvorlagen.map((dv) => dv.druckvorlageId);
      return store.getters.druckvorlagen.filter((dv: Druckvorlage) => allowedDruckvorlagenIds.includes(dv.id));
    }
  });

  const sortedDokumente = computed(() => {
    return druckvorlagen.value
      .filter((vorlage: Druckvorlage) => {
        return (
          vorlage.fileName.trim() != '' &&
          vorlage.fileName.trim().toLowerCase().includes('.dotx') &&
          (vorlage.titel.trim().toLowerCase().includes(searchString.value.trim().toLowerCase()) ||
            vorlage.kategorie.trim().toLowerCase().includes(searchString.value.trim().toLowerCase()) ||
            vorlage.fileName.trim().toLowerCase().includes(searchString.value.trim().toLowerCase()))
        );
      })
      .sort((a: Druckvorlage, b: Druckvorlage) => {
        if (sortByValue.value == 'titelasc' || sortByValue.value == 'titeldesc') {
          var titelComparison = a.titel.trim().toLowerCase().localeCompare(b.titel.trim().toLowerCase());
          if (sortByValue.value == 'titeldesc') {
            titelComparison = -1 * titelComparison;
          }

          if (titelComparison == 0) {
            var kategorieComparison = a.kategorie.trim().toLowerCase().localeCompare(b.kategorie.trim().toLowerCase());
            return kategorieComparison;
          }

          return titelComparison;
        }

        if (sortByValue.value == 'kategorieasc' || sortByValue.value == 'kategoriedesc') {
          var kategorieComparison = a.kategorie.trim().toLowerCase().localeCompare(b.kategorie.trim().toLowerCase());
          if (sortByValue.value == 'kategoriedesc') {
            kategorieComparison = -1 * kategorieComparison;
          }

          if (kategorieComparison == 0) {
            var titelComparison = a.titel.trim().toLowerCase().localeCompare(b.titel.trim().toLowerCase());
            return titelComparison;
          }

          return kategorieComparison;
        }

        if (sortByValue.value == 'dateiasc' || sortByValue.value == 'dateidesc') {
          var dateiComparison = a.fileName.trim().toLowerCase().localeCompare(b.fileName.trim().toLowerCase());
          if (sortByValue.value == 'dateidesc') {
            dateiComparison = -1 * dateiComparison;
          }

          if (dateiComparison == 0) {
            var titelComparison = a.titel.trim().toLowerCase().localeCompare(b.titel.trim().toLowerCase());
            return titelComparison;
          }

          return dateiComparison;
        }

        return 0;
      });
  });

  const groupedItems = computed<GroupedDruckvorlagen>(() => {
    const noGroup: Druckvorlage[] = [];

    const data = sortedDokumente.value.reduce((groups: GroupedDruckvorlagen, vorlage: Druckvorlage) => {
      if (!vorlage.kategorie) {
        noGroup.push(vorlage);
      } else {
        const groupsList = vorlage.kategorie.split(';');

        for (let group of groupsList) {
          group = group.trim();

          if (group === '') {
            continue;
          }

          const existingGroup = groups[group];

          if (existingGroup) {
            existingGroup.push(vorlage);
          } else {
            groups[group] = [vorlage];
          }
        }
      }

      return groups;
    }, {});

    // Sort keys alphabetically
    const sortedData: GroupedDruckvorlagen = {};
    Object.keys(data)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      .forEach((key) => {
        sortedData[key] = data[key];
      });

    if (noGroup.length > 0) {
      sortedData['Keine Gruppe'] = noGroup;
    }

    return sortedData;
  });

  async function generateDocument(documentId: number) {
    // console.log(props.dokument.data);
    var documentText = prepareDocumentText(props.dokument.data);

    // console.log(documentText);
    // return;

    await store
      .dispatch(
        VertragActionTypes.GenerateWordDocument,
        new GenerateWordDocumentRequest(props.titel, props.vertragspartner2Name, documentText, documentId, props.qrCodeData, props.dokument.fileName + '.docx')
      )
      .then(() => {
        closeModal();
      });
  }

  // Variablen-Platzhalter aus dem Dokument entfernen
  function prepareDocumentText(documentText: string): string {
    // Temporär ein DOM-Element erstellen, um mit DOM-Methoden zu arbeiten
    const parser = new DOMParser();
    const doc = parser.parseFromString(documentText, 'text/html');

    // Alle <p>-Elemente durchgehen
    const paragraphs = Array.from(doc.querySelectorAll('p'));
    paragraphs.forEach((p) => {
      const spanMatches = Array.from(p.querySelectorAll('span')).filter((span) => {
        return span.style.backgroundColor === 'yellow' && /^<<.*>>$/.test(span.textContent || '');
      });

      spanMatches.forEach((span) => {
        // Prüfe, ob <span> von anderen Tags umgeben ist (z.B. <strong>, <em>, etc.)
        let emptyParent = null;
        if (span.parentElement && span.parentElement !== p) {
          // Überprüfen, ob Parent außer diesem span keine weiteren Inhalte hat
          const parent = span.parentElement;
          if (parent.textContent?.trim() === span.textContent?.trim()) {
            emptyParent = parent;
          }
        }
        // Prüfe, ob direkt nach dem <span> ein <br> steht
        const next = emptyParent != null ? emptyParent.nextSibling : span.nextSibling;
        if (next && next.nodeName === 'BR') {
          next.remove();
        }
        span.remove();

        // Wenn der Parent-Tag jetzt leer ist, diesen auch entfernen
        if (emptyParent && emptyParent.textContent?.trim() === '') {
          emptyParent.remove();
        }
      });

      // Wenn nach dem Entfernen keine Inhalte mehr im <p>-Tag sind → <p> entfernen
      if (p.textContent?.trim() === '') {
        p.remove();
      }
    });

    // Den bereinigten HTML-Code zurückgeben (nur body-Inhalt)
    return doc.body.innerHTML;
  }

  //#region Modal
  const downloadVertragDocumentModalRef = ref();
  function closeModal() {
    Modal.getInstance(downloadVertragDocumentModalRef.value)?.hide();
  }
  function showModal() {
    if (downloadVertragDocumentModalRef.value) {
      new Modal(downloadVertragDocumentModalRef.value).show();
    }
  }
  //#endregion
</script>

<style scoped>
  .select-word-vorlage:hover {
    cursor: pointer;
    background-color: #fff4af !important;
  }

  .modal-body {
    background: rgb(242, 243, 248);
  }

  .modal-body-min-height {
    min-height: 400px;
  }

  .table-col:hover {
    background: rgb(216, 217, 222);
    cursor: pointer;
  }

  .table-sort-icon {
    font-size: 0.7em;
  }

  .select-sort:hover {
    cursor: pointer;
  }
</style>
